const FALLBACK_SIGNED_AMZ_UPLOAD_HEADERS = new Set([
  'x-amz-server-side-encryption',
]);

const readSignedUploadHeaderNames = (uploadUrl: string): Set<string> | null => {
  try {
    const signedHeaders = new URL(uploadUrl).searchParams.get('X-Amz-SignedHeaders');
    if (!signedHeaders) return null;
    return new Set(
      signedHeaders
        .split(';')
        .map((header) => header.trim().toLowerCase())
        .filter((header) => header && header !== 'host'),
    );
  } catch {
    return null;
  }
};

const hasHeader = (headers: Record<string, string>, headerName: string): boolean => (
  Object.keys(headers).some((name) => name.toLowerCase() === headerName.toLowerCase())
);

const shouldSendBrowserHeader = (
  headerName: string,
  signedHeaderNames: Set<string> | null,
): boolean => {
  const normalized = headerName.trim().toLowerCase();
  if (!normalized.startsWith('x-amz-')) {
    return true;
  }
  if (signedHeaderNames) {
    return signedHeaderNames.has(normalized);
  }
  return FALLBACK_SIGNED_AMZ_UPLOAD_HEADERS.has(normalized);
};

export const buildBrowserUploadHeaders = ({
  uploadHeaders,
  uploadUrl,
  fileType,
}: {
  uploadHeaders?: Record<string, string>;
  uploadUrl: string;
  fileType: string;
}): Record<string, string> => {
  const candidates: Record<string, string> = {};
  for (const [name, value] of Object.entries(uploadHeaders ?? {})) {
    const normalizedName = name.trim();
    const normalizedValue = String(value ?? '').trim();
    if (!normalizedName || !normalizedValue) continue;
    candidates[normalizedName] = normalizedValue;
  }

  if (!hasHeader(candidates, 'Content-Type')) {
    candidates['Content-Type'] = fileType || 'application/octet-stream';
  }

  const signedHeaderNames = readSignedUploadHeaderNames(uploadUrl);
  return Object.fromEntries(
    Object.entries(candidates).filter(([name]) => shouldSendBrowserHeader(name, signedHeaderNames)),
  );
};
